import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, Info, Pencil, Play } from 'lucide-react';
import { reportsAPI } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { useAppStore } from '../../stores/useAppStore';
import { canManageReports } from '../../utils/accessControl';
import { downloadResponse, slugify } from '../../utils/download';
import { readParam, useSyncUrlParams } from '../../hooks/useUrlListState';
import { Button } from '../ui';
import DateRangeControl from './DateRangeControl';
import ResultsGrid from './ResultsGrid';
import { extractApiError, summarise } from './lib/apiErrors';
import { groupRef, indexDataset } from './lib/catalog';
import { useReportCatalog } from './useReportCatalog';
import { useReportRun, userTimezone } from './useReportRun';

function KindBadge({ kind }) {
  const cls = kind === 'sql' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800';
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{kind === 'sql' ? 'SQL' : 'Builder'}</span>;
}

/**
 * Run page for a saved report: pick a date range, run, page through results, export.
 */
const ReportRunner = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addNotification } = useAppStore();
  const isAdmin = canManageReports(user);
  const { datasetByKey, presets } = useReportCatalog();
  const [searchParams] = useSearchParams();

  const [report, setReport] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [exporting, setExporting] = useState(null);

  const [dateRange, setDateRange] = useState(() => ({
    field: readParam(searchParams, 'field', null) || null,
    preset: readParam(searchParams, 'preset', ''),
    start: readParam(searchParams, 'start', null) || null,
    end: readParam(searchParams, 'end', null) || null,
  }));
  const [sortOverride, setSortOverride] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await reportsAPI.get(id);
        if (cancelled) return;
        setReport(res.data);
        setDateRange((dr) => {
          if (dr.preset) return dr;
          const saved = res.data.Kind === 'builder' ? res.data.Definition?.date_range : null;
          const preset = saved?.preset || res.data.Parameters?.default_preset || 'this_month';
          return { field: dr.field || saved?.field || null, preset, start: saved?.start || null, end: saved?.end || null };
        });
      } catch (err) {
        if (cancelled) return;
        const apiErr = await extractApiError(err);
        if (apiErr.status === 404) setNotFound(true);
        else if (apiErr.status === 403) {
          addNotification({ type: 'error', message: "You don't have permission to run this report" });
          navigate('/reports');
        } else setLoadError(summarise(apiErr));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const datasetNode = report?.Kind === 'builder' ? datasetByKey.get(report.Definition?.dataset) : null;
  const index = useMemo(() => indexDataset(datasetNode), [datasetNode]);
  const groupBy = useMemo(() => (report?.Kind === 'builder' ? (report.Definition?.group_by || []).map((g) => (typeof g === 'string' ? g : groupRef(g))) : []), [report]);
  const usesDateRange = report?.Kind === 'builder' || !!report?.Parameters?.uses_date_range;

  const buildParams = useCallback(
    (base) => ({
      ...base,
      date_range: usesDateRange && dateRange.preset ? { field: dateRange.field || null, preset: dateRange.preset, start: dateRange.start || null, end: dateRange.end || null } : undefined,
      sort: sortOverride.length ? sortOverride : undefined,
    }),
    [usesDateRange, dateRange, sortOverride]
  );

  const execute = useCallback((base) => reportsAPI.run(id, buildParams(base)), [id, buildParams]);
  const key = JSON.stringify({ id, dateRange, sortOverride });
  const run = useReportRun({ execute, key, initialPageSize: readParam(searchParams, 'size', 50, 'number') });

  useSyncUrlParams(
    { page: run.page, size: run.pageSize, preset: dateRange.preset, start: dateRange.start, end: dateRange.end, field: dateRange.field },
    { page: 1, size: 50, preset: '', start: null, end: null, field: null }
  );

  // Auto-run once the report (and its default range) is known.
  const [autoRan, setAutoRan] = useState(false);
  useEffect(() => {
    if (report && dateRange.preset && !autoRan) {
      setAutoRan(true);
      run.run();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, dateRange.preset, autoRan]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        run.run();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [run]);

  const onSort = (colId) => {
    setSortOverride((prev) => {
      const current = prev.length === 1 && prev[0].by === colId ? prev[0] : null;
      if (!current) return [{ by: colId, dir: 'asc' }];
      if (current.dir === 'asc') return [{ by: colId, dir: 'desc' }];
      return [];
    });
  };
  // Re-run when the sort override changes after an initial run.
  useEffect(() => {
    if (autoRan && run.status !== 'idle') run.run({ resetPage: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOverride]);

  const doExport = async (format) => {
    setExporting(format);
    try {
      const params = buildParams({ user_timezone: userTimezone() });
      const res = await reportsAPI.export(id, format, params);
      const range = run.result?.date_range_applied;
      const fallback = `${slugify(report.Name)}${range ? `_${range.start_local}_${range.end_local}` : ''}.${format}`;
      downloadResponse(res, fallback);
    } catch (err) {
      addNotification({ type: 'error', message: summarise(await extractApiError(err)) });
    } finally {
      setExporting(null);
    }
  };

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Report not found</h1>
        <p className="text-gray-600 mb-6">It may have been deleted or deactivated.</p>
        <Button onClick={() => navigate('/reports')}>Back to reports</Button>
      </div>
    );
  }
  if (loadError) {
    return <div className="max-w-3xl mx-auto px-4 py-16 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg">{loadError}</div>;
  }
  if (!report) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <button type="button" onClick={() => navigate('/reports')} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-800 mb-1">
            <ArrowLeft className="w-4 h-4 mr-1" /> Reports
          </button>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            {report.Name}
            <KindBadge kind={report.Kind} />
            {datasetNode && <span className="text-xs font-normal text-gray-500">{datasetNode.label}</span>}
          </h1>
          {!report.IsActive && <p className="mt-2 inline-block rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">This report is inactive.</p>}
          {report.valid === false && (
            <p className="mt-2 rounded bg-red-50 px-2 py-1 text-xs text-red-700">This report's definition no longer validates: {report.errors?.[0]}</p>
          )}
        </div>
        {isAdmin && (
          <Button variant="secondary" onClick={() => navigate(`/reports/${id}/edit`)}>
            <Pencil className="w-4 h-4 mr-2" /> Edit
          </Button>
        )}
      </div>

      {report.Description && (
        <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <Info className="w-4 h-4 mt-0.5 shrink-0 text-blue-500" />
          <p>{report.Description}</p>
        </div>
      )}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 flex flex-wrap items-end justify-between gap-4">
        {usesDateRange ? (
          <DateRangeControl
            compact
            value={dateRange}
            onChange={(patch) => setDateRange((dr) => ({ ...dr, ...patch, ...(patch.preset && patch.preset !== 'custom' ? { start: null, end: null } : {}) }))}
            presets={presets}
            dateFields={index.dateFields}
            showFieldSelect={report.Kind === 'builder' && index.dateFields.length > 1}
            defaultFieldLabel={datasetNode?.date_scope === 'rollups' ? 'activity: sales, returns, purchases' : null}
            applied={run.result?.date_range_applied}
            allowAll={report.Kind === 'builder'}
          />
        ) : (
          <p className="text-sm text-gray-500">This report has no parameters.</p>
        )}
        <div className="flex items-center gap-2">
          <Button onClick={() => run.run()} disabled={run.isRunning} title="Ctrl+Enter">
            <Play className="w-4 h-4 mr-2" /> {run.isRunning ? 'Running…' : 'Run'}
          </Button>
          <Button variant="secondary" onClick={() => doExport('csv')} disabled={!!exporting}>
            <Download className="w-4 h-4 mr-2" /> {exporting === 'csv' ? 'Exporting…' : 'CSV'}
          </Button>
          <Button variant="secondary" onClick={() => doExport('xlsx')} disabled={!!exporting}>
            <Download className="w-4 h-4 mr-2" /> {exporting === 'xlsx' ? 'Exporting…' : 'Excel'}
          </Button>
        </div>
      </div>

      {run.isStale && run.result && (
        <p className="text-xs rounded bg-amber-100 text-amber-800 px-2 py-1 inline-block">Parameters changed — run to refresh.</p>
      )}
      {run.result?.total_rows > run.pageSize && (
        <p className="text-xs text-gray-500">Showing one page of {run.result.total_rows.toLocaleString()} rows. Use Export for the full result.</p>
      )}
      {run.result?.warnings?.length > 0 && (
        <div className="rounded bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">{run.result.warnings.join(' ')}</div>
      )}

      <ResultsGrid
        result={run.result}
        status={run.status}
        error={run.error}
        groupBy={groupBy}
        sort={sortOverride}
        onSort={report.Kind === 'builder' ? onSort : undefined}
        page={run.page}
        pageSize={run.pageSize}
        onPageChange={(p) => run.setPage(p)}
        onPageSizeChange={(s) => run.setPageSize(s)}
        stale={run.isStale}
      />
    </div>
  );
};

export default ReportRunner;
